import React, { Component } from "react";
import PropTypes from "prop-types";
import defaultStateReducer from "./defaultStateReducer";
import Downshift from "downshift";
import dropRight from "lodash/fp/dropRight";
import get from "lodash/fp/get";
import uniqBy from "lodash/fp/uniqBy";
import TagsContainer from "./TagsContainer";
import Tags from "./Tags";
import noop from "lodash/fp/noop";
import TagsPack from "./TagsPack";
import Tag, { TagText } from "./Tag";
import VendorTag from "./VendorTag";
import TagsAutosizeInput from "./TagsAutosizeInput";
import TagsMenu from "./TagsMenu";
import Clear from "./Clear";
import TagsMenuHint from "./TagsMenuHint";
import SharedRef from "./SharedRef";

export default class MultiVendorSelect extends Component {
  static propTypes = {
    defaultValue: PropTypes.array,
    vendors: PropTypes.array.isRequired,
    selectableVendors: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    inputValue: PropTypes.string.isRequired,
    onInputValueChange: PropTypes.func.isRequired,
    itemToString: PropTypes.func,
    disabled: PropTypes.bool,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    error: PropTypes.string,
    css: PropTypes.object,
    placeholder: PropTypes.string,
    borderColor: PropTypes.string,
  };

  static defaultProps = {
    defaultValue: [],
    itemToString: get("objectID"),
    disabled: false,
    onFocus: noop,
    onBlur: noop,
    error: null,
    css: {},
    placeholder: "Type vendor names...",
    borderColor: null,
  };

  handleKeyDown = e => {
    const { vendors, onChange, inputValue } = this.props;

    if (e.key === "Backspace" && inputValue.length === 0 && vendors.length > 0) {
      onChange(dropRight(1, vendors));
    }
  };

  handleRemoveVendor = (e, vendor) => {
    e.persist();

    const { vendors, onChange } = this.props;

    onChange(vendors.filter(maybeProduct => maybeProduct !== vendor));

    const focusables = Array.from(this.tagsRef.querySelectorAll('[tabindex="0"]'));

    const position = focusables.findIndex(focusable => focusable === e.target);
    const element = focusables[position > 0 ? position - 1 : 1];

    if (vendors.length > 1 && element) {
      element.focus();
    } else if (vendors.length === 1 && element) {
      this.inputRef.focus();
    }
  };

  handleClearKeyDown = e => {
    if (e.key === "Enter") {
      const { onChange } = this.props;

      onChange([]);
    }
  };

  handleChange = newValue => {
    const { vendors, onChange, onInputValueChange } = this.props;

    onChange(vendors.concat(newValue));
    onInputValueChange("");
  };

  render() {
    const {
      defaultValue,
      vendors,
      selectableVendors,
      onChange,
      inputValue,
      onInputValueChange,
      itemToString,
      disabled,
      onFocus,
      onBlur,
      placeholder,
      borderColor,
      error,
      ...props
    } = this.props;

    const validationState = error ? "error" : null;

    return (
      <Downshift
        itemToString={itemToString}
        defaultSelectedItem={defaultValue}
        onChange={this.handleChange}
        inputValue={inputValue}
        selectedItem={vendors}
        selectedItemChanged={(a, b) => {
          if (a && b) {
            return a.length !== b.length;
          }

          return a !== b;
        }}
        stateReducer={defaultStateReducer}
      >
        {({
          getInputProps,
          getRootProps,
          getItemProps,
          selectedItem: selectedItems,
          highlightedIndex,
          isOpen,
          inputValue,
        }) => {
          const vendors = selectableVendors.filter(
            ({ objectID }) => !selectedItems.find(({ objectID: vendorId }) => vendorId === objectID),
          );

          return (
            <TagsContainer
              {...getRootProps({
                refKey: "innerRef",
              })}
              {...props}
            >
              <SharedRef>
                {({ current, innerRef }) => (
                  <Tags
                    isOpen={isOpen || validationState === "error"}
                    validationState={validationState}
                    disabled={disabled}
                    innerRef={node => {
                      this.tagsRef = node;
                    }}
                    css={
                      borderColor
                        ? {
                            borderColor,
                          }
                        : null
                    }
                  >
                    <TagsPack validationState={validationState}>
                      {uniqBy(vendor => vendor._id || vendor.objectID, selectedItems).map(vendor => {
                        return (
                          <VendorTag
                            vendor={vendor}
                            key={vendor._id || vendor.objectID}
                            onClose={e => this.handleRemoveVendor(e, vendor)}
                            disabled={disabled}
                          />
                        );
                      })}

                      <TagsAutosizeInput
                        disabled={disabled}
                        inputRef={node => {
                          innerRef(node);
                          this.inputRef = node;
                        }}
                        value={inputValue}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        tabIndex={0}
                        {...getInputProps({
                          placeholder: selectedItems.length === 0 ? placeholder : null,
                          "aria-label": "Type vendor names",
                          onKeyDown: this.handleKeyDown,
                          onChange: e => {
                            onInputValueChange(e.target.value);
                          },
                        })}
                      />
                    </TagsPack>

                    <Clear
                      onKeyDown={e => {
                        this.handleClearKeyDown(e);
                        current().focus();
                      }}
                      onClick={() => {
                        onChange([]);
                        current().focus();
                      }}
                    />
                  </Tags>
                )}
              </SharedRef>

              {(isOpen || validationState === "error") && (
                <TagsMenu validationState={validationState}>
                  <TagsPack validationState={validationState}>
                    {vendors.map((vendor, index) => {
                      return (
                        <Tag
                          key={vendor.objectID}
                          {...getItemProps({
                            index,
                            item: vendor,
                            active: index === highlightedIndex,
                          })}
                        >
                          <TagText>{vendor.name}</TagText>
                        </Tag>
                      );
                    })}
                  </TagsPack>

                  {error && <TagsMenuHint>{error}</TagsMenuHint>}

                  {vendors.length === 0 && <TagsMenuHint>No vendors found</TagsMenuHint>}
                </TagsMenu>
              )}
            </TagsContainer>
          );
        }}
      </Downshift>
    );
  }
}
